// Write an email: sections on the left, the email in the middle (edit words in place), options on the right.
// Saves by itself a second after each change. Undo/redo keeps 100 steps.

import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError, errorText } from '../lib/api';
import { navigate, useLeaveGuard } from '../lib/router';
import { Button, IconButton, Notice, Segmented, Spinner, confirmBox, toast, toastError } from '../lib/ui';
import { Icon, type IconName } from '../lib/icons';
import { useSettingsState } from '../lib/store';
import { draftAction, dropDraft, keepDraft, readDraft } from '../lib/draft';
import { fillName, previewHtml } from '../lib/preview';
import { when } from '../lib/format';
import { BLOCK_INFO, cloneBlock, createBlock, type Block, type BlockType, type Img } from '../../shared/blocks';
import { slotAspect } from '../../shared/render';
import type { EmailRow } from '../../shared/types';
import { Canvas, revealBlock, type FrameAction } from './Canvas';
import { BlockPanel, EmailPanel, type DocFields } from './Inspector';
import { ImagePicker, uploadPhoto } from './ImagePicker';
import { InboxPreview } from '../parts/InboxPreview';
import { insertAt, moveBlock, moveBlockTo, setPath, updateBlock } from './paths';
import { armDrag, type DropAction } from './drag';

interface Doc extends DocFields {
  blocks: Block[];
}

type SaveState = 'saved' | 'saving' | 'dirty' | 'error' | 'conflict';

export const BLOCK_ICON: Record<BlockType, IconName> = {
  hero: 'image',
  heading: 'heading',
  text: 'type',
  image: 'image',
  button: 'button',
  split: 'split',
  tiles: 'columns',
  timeline: 'timeline',
  prices: 'price',
  offer: 'gift',
  quote: 'quote',
  divider: 'minus',
  spacer: 'space',
};

const WIDTHS = { desktop: 660, phone: 375 };

export function Editor({ id }: { id: number }) {
  const { bundle, error: settingsErr, retry: retrySettings } = useSettingsState();
  const settings = bundle?.settings ?? null;
  const [email, setEmail] = useState<EmailRow | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [doc, setDoc] = useState<Doc | null>(null);
  const docRef = useRef<Doc | null>(null);
  const [version, setVersion] = useState(0);
  const [sel, setSel] = useState<string | null>(null);
  const [view, setView] = useState<'desktop' | 'phone'>(() => (innerWidth < 900 ? 'phone' : 'desktop'));
  const [save, setSave] = useState<SaveState>('saved');
  const [saveMsg, setSaveMsg] = useState('');
  const [picker, setPicker] = useState<{ id: string; path: string } | null>(null);
  const [sheet, setSheet] = useState<'add' | 'panel' | null>(null);
  const [testing, setTesting] = useState(false);
  const [more, setMore] = useState(false);
  const [tapNote, setTapNote] = useState(() => {
    try {
      return localStorage.getItem('lavir.tapnote') !== 'seen';
    } catch {
      return true;
    }
  });
  const hideTapNote = () => {
    setTapNote(false);
    try {
      localStorage.setItem('lavir.tapnote', 'seen');
    } catch {
      // Private window: it shows again next time, which is fine.
    }
  };
  // The phone tip goes away the first time she taps something in the email.
  useEffect(() => {
    if (sel && tapNote) hideTapNote();
  }, [sel]);
  const [kept, setKept] = useState<Doc | null>(null); // unsaved changes from earlier, made on an older version
  const [, bump] = useState(0);

  const updatedAt = useRef('');
  const past = useRef<Doc[]>([]);
  const future = useRef<Doc[]>([]);
  const lastKey = useRef({ key: '', at: 0 });
  const timer = useRef<number | undefined>(undefined);
  const dirty = useRef(false);
  const inflight = useRef<Promise<void> | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const reveal = useRef<string | null>(null);
  const alive = useRef(true);

  // ----- load -----
  useEffect(() => {
    api<{ email: EmailRow }>(`/api/emails/${id}`)
      .then(({ email }) => {
        if (email.status !== 'draft' && email.status !== 'scheduled') {
          navigate(`/emails/${id}`, { replace: true });
          return;
        }
        const d: Doc = { name: email.name, subject: email.subject, preheader: email.preheader, look: email.look, blocks: email.blocks };
        updatedAt.current = email.updated_at;
        docRef.current = d;
        setEmail(email);
        setDoc(d);
        // Changes that never reached the server (failed save, signed out, tab closed) are kept on this device.
        const saved = readDraft<Doc>(id);
        const todo = draftAction(saved, { doc: d, updated_at: email.updated_at });
        if (todo === 'drop') dropDraft(id);
        else if (todo === 'restore') {
          putBack(saved!.doc);
          toast('Your unsaved changes are back.', 'info');
        } else setKept(saved!.doc);
      })
      .catch((e) => setLoadErr(errorText(e)));
  }, [id]);

  // ----- saving -----
  /** Saves now. True once the server has everything typed so far, false if a save failed. */
  const flush = useCallback(async (all = false): Promise<boolean> => {
    clearTimeout(timer.current);
    if (inflight.current) await inflight.current;
    if (!docRef.current) return false;
    if (!dirty.current) return true;
    dirty.current = false;
    const d = docRef.current;
    setSave('saving');
    let ok = false;
    inflight.current = api<{ updated_at: string; unscheduled: boolean }>(`/api/emails/${id}`, { method: 'PUT', body: { ...d, updated_at: updatedAt.current } })
      .then((r) => {
        ok = true;
        updatedAt.current = r.updated_at;
        setSave(dirty.current ? 'dirty' : 'saved');
        if (docRef.current === d) dropDraft(id);
        else keepDraft(id, docRef.current, r.updated_at);
        if (r.unscheduled) {
          toast('Taken off the schedule because it changed. Check and schedule it again when ready.', 'info');
          setEmail((e) => (e ? { ...e, status: 'draft', scheduled_at: null } : e));
        }
      })
      .catch((e) => {
        // Not saved: the changes stay marked unsaved (and kept on this device) whatever went wrong.
        dirty.current = true;
        setSaveMsg(errorText(e));
        if (e instanceof ApiError && e.status === 409) setSave('conflict');
        else {
          setSave('error');
          if (alive.current && !(e instanceof ApiError && e.status === 401)) timer.current = window.setTimeout(() => flush(), 6000);
        }
      })
      .finally(() => {
        inflight.current = null;
      });
    await inflight.current;
    if (!ok) return false;
    if (dirty.current) return all ? flush(true) : false; // typed while saving
    return true;
  }, [id]);

  const scheduleSave = () => {
    dirty.current = true;
    if (docRef.current) keepDraft(id, docRef.current, updatedAt.current);
    setSave((s) => (s === 'conflict' ? s : 'dirty'));
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => flush(), 1000);
  };

  // Leaving waits for the save. If it fails, Jen chooses; the changes stay on this device either way.
  useLeaveGuard(
    useCallback(() => {
      if (!dirty.current && !inflight.current) return true;
      return flush(true).then(
        (ok) =>
          ok ||
          confirmBox({
            title: 'Your last changes are not saved',
            body: <p>They stay on this device and come back when you open this email again.</p>,
            ok: 'Leave anyway',
          }),
      );
    }, [flush]),
  );

  useEffect(
    () => () => {
      alive.current = false;
      clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    const before = (e: BeforeUnloadEvent) => {
      if (!dirty.current || !docRef.current) return;
      fetch(`/api/emails/${id}`, {
        method: 'PUT',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...docRef.current, updated_at: updatedAt.current }),
      });
      e.preventDefault();
    };
    addEventListener('beforeunload', before);
    return () => removeEventListener('beforeunload', before);
  }, [id]);

  // ----- changes and history -----
  const change = (next: Doc, key: string, redraw: boolean) => {
    const prev = docRef.current!;
    const now = Date.now();
    if (!(key && key === lastKey.current.key && now - lastKey.current.at < 1500)) {
      past.current.push(prev);
      if (past.current.length > 100) past.current.shift();
      future.current = [];
    }
    lastKey.current = { key, at: now };
    docRef.current = next;
    setDoc(next);
    if (redraw) setVersion((v) => v + 1);
    scheduleSave();
  };

  const restore = (d: Doc) => {
    docRef.current = d;
    setDoc(d);
    setVersion((v) => v + 1);
    lastKey.current = { key: '', at: 0 };
    if (sel && !d.blocks.some((b) => b.id === sel)) setSel(null);
    scheduleSave();
    bump((n) => n + 1);
  };
  const undo = () => {
    const d = past.current.pop();
    if (!d) return;
    future.current.push(docRef.current!);
    restore(d);
  };
  const redo = () => {
    const d = future.current.pop();
    if (!d) return;
    past.current.push(docRef.current!);
    restore(d);
  };
  /** Put kept changes back. Undo returns to what the server had. */
  const putBack = (d: Doc) => {
    setKept(null);
    change(d, '', true);
  };
  const throwAway = () => {
    setKept(null);
    dropDraft(id);
  };

  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, select, [contenteditable]')) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        (e.shiftKey ? redoRef.current : undoRef.current)();
      } else if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redoRef.current();
      }
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, []);

  // ----- block operations -----
  const setBlocks = (blocks: Block[], key: string, redraw = true) => change({ ...docRef.current!, blocks }, key, redraw);

  const addBlock = (type: BlockType) => {
    const blocks = docRef.current!.blocks;
    const b = createBlock(type);
    const at = sel ? blocks.findIndex((x) => x.id === sel) + 1 : blocks.length;
    const next = blocks.slice();
    next.splice(at || blocks.length, 0, b);
    setBlocks(next, '');
    setSel(b.id);
    reveal.current = b.id;
    setSheet(null);
  };

  const act = (a: FrameAction, bid: string) => {
    const blocks = docRef.current!.blocks;
    if (a === 'up' || a === 'down') {
      setBlocks(moveBlock(blocks, bid, a === 'up' ? -1 : 1), '');
      reveal.current = bid;
    } else if (a === 'copy') {
      const i = blocks.findIndex((b) => b.id === bid);
      const copy = cloneBlock(blocks[i]);
      const next = blocks.slice();
      next.splice(i + 1, 0, copy);
      setBlocks(next, '');
      setSel(copy.id);
      reveal.current = copy.id;
    } else if (a === 'delete') {
      if (blocks.length <= 1) {
        toast('An email needs at least one section.', 'info');
        return;
      }
      setBlocks(
        blocks.filter((b) => b.id !== bid),
        '',
      );
      setSel(null);
      toast('Section deleted.', 'info', { label: 'Undo', run: () => undoRef.current() });
    }
  };

  // After a redraw, scroll a new or moved section into view.
  useEffect(() => {
    if (!reveal.current) return;
    const rid = reveal.current;
    reveal.current = null;
    setTimeout(() => revealBlock(frameRef.current, scroller.current, rid), 60);
  }, [version]);

  const onEdit = (bid: string, path: string, value: string) => {
    const blocks = updateBlock(docRef.current!.blocks, bid, (b) => setPath(b, path, value));
    setBlocks(blocks, `edit:${bid}|${path}`, false);
  };

  // ----- drag and drop -----
  const onDrop = async (a: DropAction) => {
    const blocks = docRef.current!.blocks;
    if (a.kind === 'add') {
      const b = createBlock(a.type);
      setBlocks(insertAt(blocks, b, a.index), '');
      setSel(b.id);
      reveal.current = b.id;
      return;
    }
    if (a.kind === 'move') {
      const moved = moveBlockTo(blocks, a.id, a.index);
      if (moved === blocks) return;
      setBlocks(moved, '');
      setSel(a.id);
      reveal.current = a.id;
      return;
    }
    // Photos from the computer: fit and upload first, then place them.
    // Jen may keep typing meanwhile, so remember the spot by the section below it, not by number.
    const files = a.kind === 'replace' ? [a.file] : a.files.slice(0, 6);
    toast(files.length > 1 ? `Adding ${files.length} photos…` : 'Adding your photo…', 'info');
    try {
      if (a.kind === 'replace') {
        const type = docRef.current!.blocks.find((b) => b.id === a.id)?.type ?? 'image';
        const img = await uploadPhoto(a.file, slotAspect(type));
        if (!docRef.current!.blocks.some((b) => b.id === a.id)) return;
        setBlocks(updateBlock(docRef.current!.blocks, a.id, (b) => setPath(b, a.path, img)), '');
        setSel(a.id);
        return;
      }
      const below = blocks[a.index]?.id ?? null;
      const imgs = [];
      for (const f of files) imgs.push(await uploadPhoto(f, null));
      const added = imgs.map((img) => ({ ...createBlock('image'), image: img }) as Block);
      const now = docRef.current!.blocks;
      const at = below ? now.findIndex((b) => b.id === below) : -1;
      let next = now;
      added.forEach((b, i) => (next = insertAt(next, b, (at < 0 ? now.length : at) + i)));
      setBlocks(next, '');
      setSel(added[0].id);
      reveal.current = added[0].id;
    } catch (e) {
      toastError(e);
    }
  };

  // A photo dropped outside the email must not open in the browser and lose the work.
  useEffect(() => {
    const stop = (e: DragEvent) => {
      if (e.dataTransfer && [...e.dataTransfer.types].includes('Files')) e.preventDefault();
    };
    addEventListener('dragover', stop);
    addEventListener('drop', stop);
    return () => {
      removeEventListener('dragover', stop);
      removeEventListener('drop', stop);
    };
  }, []);

  const pickImage = (img: Img) => {
    if (!picker) return;
    const { id: bid, path } = picker;
    setBlocks(
      updateBlock(docRef.current!.blocks, bid, (b) => setPath(b, path, img)),
      '',
    );
    setSel(bid);
    setPicker(null);
  };

  // ----- header actions -----
  const sendTest = async () => {
    setTesting(true);
    try {
      // The test must show what is on screen, so it waits for the save and stops if the save fails.
      if (!(await flush(true))) {
        toast('Not saved yet, so no test went out. Try again in a moment.', 'bad');
        return;
      }
      const r = await api<{ sentTo: number }>(`/api/emails/${id}/test`, { method: 'POST' });
      const to = settings?.test_emails[0] || settings?.from_email;
      toast(`Test sent${to ? ` to ${to}` : ''}. Check your phone.`);
      void r;
    } catch (e) {
      toastError(e);
    } finally {
      setTesting(false);
    }
  };

  const next = async () => {
    if (!(await flush(true))) {
      toast('Not saved yet. It needs to save before the next step.', 'bad');
      return;
    }
    navigate(`/emails/${id}/send`);
  };

  const html = useMemo(() => (docRef.current && settings ? previewHtml(docRef.current, settings, true) : ''), [version, settings, !!doc]);

  if (loadErr)
    return (
      <div class="wrap">
        <Notice kind="bad" action={<Button onClick={() => navigate('/')}>Back to emails</Button>}>
          {loadErr}
        </Notice>
      </div>
    );
  if (!settings && settingsErr)
    return (
      <div class="wrap">
        <Notice kind="bad" action={<Button onClick={retrySettings}>Try again</Button>}>
          {settingsErr}
        </Notice>
      </div>
    );
  if (!doc || !email || !settings) return <Spinner label="Opening your email…" />;

  const selBlock = sel ? doc.blocks.find((b) => b.id === sel) ?? null : null;
  const aspect = picker ? slotAspect(doc.blocks.find((b) => b.id === picker.id)?.type ?? 'image') : null;

  const panel = selBlock ? (
    <BlockPanel
      block={selBlock}
      settings={settings}
      onChange={(b, key) => setBlocks(updateBlock(docRef.current!.blocks, b.id, () => b), key)}
      onPickImage={(path) => setPicker({ id: selBlock.id, path })}
      onAction={(a) => act(a, selBlock.id)}
      onDone={() => setSel(null)}
    />
  ) : (
    <EmailPanel doc={doc} settings={settings} onChange={(patch, key) => change({ ...docRef.current!, ...patch }, key, 'look' in patch || 'preheader' in patch)} />
  );

  return (
    <div class="editor">
      <header class="ed-top">
        <button class="ed-back" onClick={() => navigate('/')} aria-label="Back to emails">
          <Icon name="back" size={20} />
          <span class="hide-phone">Emails</span>
        </button>
        <div class="ed-name">
          <b>{doc.name}</b>
          <SaveBadge state={save} />
        </div>
        <div class="ed-tools">
          <IconButton icon="undo" label="Undo" onClick={undo} disabled={!past.current.length} />
          <IconButton icon="redo" label="Redo" onClick={redo} disabled={!future.current.length} />
          <Segmented
            label="Preview size"
            value={view}
            onChange={setView}
            options={[
              { value: 'desktop', label: 'Computer', icon: 'desktop' },
              { value: 'phone', label: 'Phone', icon: 'phone' },
            ]}
          />
        </div>
        <div class="ed-actions">
          <Button icon="send" busy={testing} onClick={sendTest} class="hide-phone">
            Send me a test
          </Button>
          <Button kind="primary" onClick={next}>
            <span class="hide-phone">Next: check and send</span>
            <span class="show-phone">Next</span>
          </Button>
        </div>
      </header>

      {(kept || save === 'conflict' || save === 'error' || email.status === 'scheduled') && (
        <div class="ed-notices">
          {kept && (
            <Notice
              kind="warn"
              action={
                <span class="notice-actions">
                  <Button size="s" kind="primary" onClick={() => putBack(kept)}>
                    Put them back
                  </Button>
                  <Button size="s" kind="quiet" onClick={throwAway}>
                    Throw away
                  </Button>
                </span>
              }
            >
              Changes you made earlier on this device were not saved.
            </Notice>
          )}
          {save === 'conflict' && (
            <Notice kind="bad" action={<Button size="s" onClick={() => location.reload()}>Reload</Button>}>
              {saveMsg}
            </Notice>
          )}
          {save === 'error' && <Notice kind="warn">Not saved yet: {saveMsg} Trying again…</Notice>}
          {email.status === 'scheduled' && <Notice kind="info" icon="calendar">Scheduled for {when(email.scheduled_at)}. If you change anything, it comes off the schedule until you check it again.</Notice>}
        </div>
      )}

      <div class="ed-body">
        <aside class={`ed-palette ${sheet === 'add' ? 'sheet-open' : ''}`}>
          <div class="sheet-head show-phone">
            <b>Add a section</b>
            <IconButton icon="x" label="Close" onClick={() => setSheet(null)} />
          </div>
          <h3 class="hide-phone">Add a section</h3>
          <div class="palette">
            {BLOCK_INFO.filter((b) => more || MAIN_BLOCKS.includes(b.type)).map((b) => (
              <button
                key={b.type}
                class="pal-item"
                title={b.hint ? `${b.label}: ${b.hint}` : b.label}
                onClick={() => addBlock(b.type)}
                onPointerDown={(e) => armDrag(e, { kind: 'add', type: b.type, label: b.label }, { hold: 280, onStart: () => setSheet(null) })}
              >
                <span class="pal-icon">
                  <Icon name={BLOCK_ICON[b.type]} size={18} />
                </span>
                <span>
                  <b>{b.label}</b>
                  {HINTED.includes(b.type) && b.hint && <small>{b.hint}</small>}
                </span>
              </button>
            ))}
            <button class="pal-item pal-more" onClick={() => setMore(!more)} aria-expanded={more} title={more ? 'Fewer sections' : 'More sections'}>
              <span class="pal-icon">
                <Icon name={more ? 'up' : 'more'} size={18} />
              </span>
              <span>
                <b>{more ? 'Fewer sections' : 'More sections'}</b>
              </span>
            </button>
          </div>
          <p class="pal-note hide-phone">Drag a section into your email, or click it to add it below the one you selected.</p>
          <p class="pal-note show-phone">Tap to add below the selected section, or press and hold to drag it into place.</p>
        </aside>

        <div class="ed-canvas" ref={scroller} onClick={(e) => e.target === e.currentTarget && setSel(null)}>
          <div class={`paper ${view}`}>
            {/* On a computer the side panel shows her inbox. On a phone it shows here, above the email. */}
            <div class="ed-inbox">
              <InboxPreview compact fromName={settings.from_name || 'LaVir Med Spa'} subject={doc.subject} preheader={doc.preheader} />
            </div>
            {tapNote && (
              <button class="ed-tapnote" onClick={hideTapNote}>
                <Icon name="sparkle" size={16} />
                <span>Tap any words to change them. Tap a photo to swap it.</span>
                <Icon name="x" size={16} />
              </button>
            )}
            <Canvas
              html={html}
              version={version}
              width={WIDTHS[view]}
              selectedId={sel}
              frameRef={frameRef}
              onEdit={onEdit}
              onSelect={setSel}
              onImage={(bid, path) => setPicker({ id: bid, path })}
              onAction={act}
              onDrop={onDrop}
              onUndo={() => undoRef.current()}
              onRedo={() => redoRef.current()}
            />
          </div>
        </div>

        <aside class={`ed-panel ${sheet === 'panel' ? 'sheet-open' : ''}`}>
          <div class="sheet-head show-phone">
            <b>{selBlock ? 'Section options' : 'Email settings'}</b>
            <IconButton icon="x" label="Close" onClick={() => setSheet(null)} />
          </div>
          {panel}
        </aside>
      </div>

      <div class="ed-phonebar show-phone">
        <Button icon="plus" onClick={() => setSheet('add')}>
          Add section
        </Button>
        <Button icon="settings" kind={selBlock ? 'primary' : 'secondary'} onClick={() => setSheet('panel')}>
          {selBlock ? 'Options' : 'Subject'}
        </Button>
        <Button icon="send" busy={testing} onClick={sendTest}>
          Test
        </Button>
      </div>
      {sheet && <div class="sheet-scrim show-phone" onClick={() => setSheet(null)} />}

      {picker && <ImagePicker key={`${picker.id}|${picker.path}`} aspect={aspect} onPick={pickImage} onClose={() => setPicker(null)} />}
    </div>
  );
}

// The sections most emails use. The rest sit under "More sections".
const MAIN_BLOCKS: BlockType[] = ['hero', 'text', 'image', 'button', 'offer', 'quote'];
// Only these need a line under the name. The other names say what they are.
const HINTED: BlockType[] = ['timeline', 'offer', 'quote'];

function SaveBadge({ state }: { state: SaveState }) {
  if (state === 'saving' || state === 'dirty') return <span class="save-badge">Saving…</span>;
  if (state === 'saved')
    return (
      <span class="save-badge ok">
        <Icon name="check" size={13} stroke={2.6} /> Saved
      </span>
    );
  return <span class="save-badge bad">Not saved</span>;
}

export function InboxRow({ from, subject, preheader }: { from: string; subject: string; preheader: string }) {
  return (
    <div class="inbox-row" aria-label="How it looks in the inbox">
      <span class="inbox-avatar">{from.trim()[0] || 'L'}</span>
      <div>
        <div class="inbox-top">
          <b>{from}</b>
          <span>now</span>
        </div>
        <div class="inbox-subject">{fillName(subject) || <span class="muted">Your subject line</span>}</div>
        <div class="inbox-pre">{fillName(preheader) || <span class="muted">Preview text</span>}</div>
      </div>
    </div>
  );
}
